// Şub 27, 2026 — DPD Retour Raporu — server-side pagination + advanced filters
import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { useTranslation } from '../../lib/i18n';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { Package, RefreshCw, Search, Loader2, ArrowLeftRight, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { toast } from 'sonner';
import TodayIncompleteBanner from './TodayIncompleteBanner';

export default function DPDRetourReport() {
  const { t } = useTranslation();
  const [firms, setFirms] = useState([]);
  const [firmId, setFirmId] = useState('');
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [tourFilter, setTourFilter] = useState('');
  const [subcoFilter, setSubcoFilter] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  const [pcFilter, setPcFilter] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState({ items: [], total: 0, by_tour: [], by_reason_code: [], by_subco: [] });
  const [subcoList, setSubcoList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    api.get('/admin/firms').then((r) => {
      const list = r.data?.data || [];
      setFirms(list);
      const ft = list.find((f) => /fast.?trans/i.test(f.name)) || list[0];
      if (ft) setFirmId(ft.id);
    });
  }, []);

  useEffect(() => {
    if (!firmId) return;
    api.get(`/dpd/retour-subcos?firm_id=${firmId}`).then((r) => {
      setSubcoList(r.data?.data || []);
    }).catch(() => setSubcoList([]));
  }, [firmId]);

  useEffect(() => {
    if (!firmId) return;
    api.get(`/dpd/dates?firm_id=${firmId}`).then((r) => {
      const ds = r.data?.data || [];
      setDates(ds);
      if (ds.length > 0 && !selectedDate) setSelectedDate(ds[0]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmId]);

  const fetchRetour = useCallback(async () => {
    if (!firmId || !selectedDate) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        firm_id: firmId, date: selectedDate,
        skip: String(page * pageSize), limit: String(pageSize),
      });
      if (tourFilter) params.set('tour', tourFilter);
      if (subcoFilter) params.set('subco', subcoFilter);
      if (reasonFilter) params.set('reason_code', reasonFilter);
      if (pcFilter) params.set('postcode', pcFilter);
      if (q) params.set('q', q);
      const r = await api.get(`/dpd/retour?${params.toString()}`);
      setData(r.data?.data || { items: [], total: 0, by_tour: [], by_reason_code: [], by_subco: [] });
    } catch (e) {
      toast.error('Retour verisi yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [firmId, selectedDate, tourFilter, subcoFilter, reasonFilter, pcFilter, q, page, pageSize]);

  useEffect(() => { fetchRetour(); }, [fetchRetour]);

  // filtre değişince page reset
  useEffect(() => { setPage(0); }, [tourFilter, subcoFilter, reasonFilter, pcFilter, q, selectedDate, firmId, pageSize]);

  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));
  const hasActiveFilters = tourFilter || subcoFilter || reasonFilter || pcFilter || q;
  const clearFilters = () => {
    setTourFilter(''); setSubcoFilter(''); setReasonFilter(''); setPcFilter(''); setQ('');
  };

  return (
    <div className="space-y-4" data-testid="dpd-retour-page">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5 text-red-600" />{t('dpd.retourReport.title')}
          </CardTitle>
          <CardDescription>{t('dpd.retourReport.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Row 1: Firm & Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mb-2">
            <Select value={firmId} onValueChange={setFirmId}>
              <SelectTrigger data-testid="retour-firm-select"><SelectValue placeholder="Firma" /></SelectTrigger>
              <SelectContent>{firms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={selectedDate} onValueChange={setSelectedDate}>
              <SelectTrigger data-testid="retour-date-select"><SelectValue placeholder="Tarih" /></SelectTrigger>
              <SelectContent>{dates.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={fetchRetour} disabled={loading} variant="outline" data-testid="retour-refresh-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Yenile
            </Button>
          </div>

          {/* Row 2: Filters */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-2">
            <Input placeholder={t('dpd.retourReport.filters.tour')} value={tourFilter} onChange={(e) => setTourFilter(e.target.value.toUpperCase())} data-testid="retour-tour-filter" />
            <Select value={subcoFilter || '__all__'} onValueChange={(v) => setSubcoFilter(v === '__all__' ? '' : v)}>
              <SelectTrigger data-testid="retour-subco-filter"><SelectValue placeholder={t('dpd.retourReport.filters.subco')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t('dpd.retourReport.filters.subco')}</SelectItem>
                {subcoList.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder={t('dpd.retourReport.filters.reason')} value={reasonFilter} onChange={(e) => setReasonFilter(e.target.value.replace(/\D/g, ''))} data-testid="retour-reason-filter" />
            <Input placeholder={t('dpd.retourReport.filters.pc')} value={pcFilter} onChange={(e) => setPcFilter(e.target.value.replace(/\D/g, ''))} data-testid="retour-pc-filter" />
            <Input placeholder={t('dpd.retourReport.filters.q')} value={q} onChange={(e) => setQ(e.target.value)} data-testid="retour-q-filter" />
          </div>

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                <strong>{data.total.toLocaleString()}</strong> {t('dpd.common.packages').toLowerCase()}
                {hasActiveFilters && <span className="text-xs ml-2 text-orange-600">{t('dpd.retourReport.filtered')}</span>}
              </span>
              {hasActiveFilters && (
                <Button size="sm" variant="ghost" onClick={clearFilters} data-testid="retour-clear-filters" className="h-6 px-2">
                  <X className="h-3 w-3 mr-1" />{t('dpd.retourReport.clearAll')}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-gray-500">{t('dpd.common.pageSize')}:</span>
              <Select value={String(pageSize)} onValueChange={(v) => setPageSize(parseInt(v, 10))}>
                <SelectTrigger className="w-[80px] h-8" data-testid="retour-page-size"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[25, 50, 100, 200].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Şub 27, 2026 — Bugün için veri eksikliği bilgilendirmesi */}
          <TodayIncompleteBanner date={selectedDate} variant="retour" />

          {/* Aggregations chips */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <AggChip title="Tur" items={data.by_tour} onClick={(v) => setTourFilter(v)} labelKey="tour" />
            <AggChip title="Sebep Kodu" items={data.by_reason_code} onClick={(v) => setReasonFilter(v)} labelKey="code" showLabel />
            <AggChip title="Subco" items={data.by_subco} onClick={(v) => setSubcoFilter(v)} labelKey="subco" />
          </div>

          {/* Table */}
          <div className="rounded-lg border overflow-x-auto">
            <Table className="text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[140px]"><Package className="inline h-3 w-3 mr-1" />Paket No</TableHead>
                  <TableHead className="w-[65px]">Tur</TableHead>
                  <TableHead className="w-[75px]">Saat</TableHead>
                  <TableHead className="w-[230px]">Sebep</TableHead>
                  <TableHead className="w-[55px] text-right">Kg</TableHead>
                  <TableHead className="w-[65px]">Posta</TableHead>
                  <TableHead>Alıcı</TableHead>
                  <TableHead className="w-[100px]">Subco</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>{Array.from({ length: 8 }).map((__, j) => (
                    <TableCell key={j}><Skeleton className="h-3 w-full" /></TableCell>
                  ))}</TableRow>
                ))}
                {!loading && data.items.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center text-gray-500 py-8">
                    {t('dpd.retourReport.empty')}
                  </TableCell></TableRow>
                )}
                {!loading && data.items.map((it) => (
                  <TableRow key={it.id} data-testid={`retour-row-${it.pakketnummer}`}>
                    <TableCell className="font-mono truncate" title={it.pakketnummer}>{it.pakketnummer}</TableCell>
                    <TableCell><Badge variant="outline" className="font-mono text-xs">{it.tour || '-'}</Badge></TableCell>
                    <TableCell className="text-gray-600">{it.uur ? `${it.uur.slice(0, 2)}:${it.uur.slice(2, 4)}` : '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Badge variant="outline" className="text-[10px] font-mono">{it.bijkomende_code_2 || '—'}</Badge>
                        <span className="text-[11px] truncate" title={it.reason_label}>{it.reason_label}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{it.gewicht ? (it.gewicht / 1000).toFixed(1) : '-'}</TableCell>
                    <TableCell>{it.postcode}</TableCell>
                    <TableCell className="truncate max-w-[280px]" title={it.ontvanger}>{it.ontvanger}</TableCell>
                    <TableCell className="truncate" title={it.subco}>{it.subco || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-3">
            <div className="text-xs text-gray-500">
              {data.total > 0 ? (
                <>Gösterilen: <strong>{page * pageSize + 1}</strong> - <strong>{Math.min((page + 1) * pageSize, data.total)}</strong> / {data.total.toLocaleString('tr-TR')}</>
              ) : 'Kayıt yok'}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setPage(0)} disabled={page === 0 || loading} data-testid="retour-first-page">İlk</Button>
              <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0 || loading} data-testid="retour-prev-page">
                <ChevronLeft className="h-3 w-3" />
              </Button>
              <span className="text-xs px-2">Sayfa {page + 1} / {totalPages}</span>
              <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1 || loading} data-testid="retour-next-page">
                <ChevronRight className="h-3 w-3" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1 || loading} data-testid="retour-last-page">Son</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AggChip({ title, items, onClick, labelKey, showLabel }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs font-medium text-gray-500 mb-2">{title} Başına ({items.length})</div>
      <div className="flex flex-wrap gap-1.5 max-h-24 overflow-auto">
        {items.slice(0, 20).map((x) => (
          <button
            key={x[labelKey] || 'unknown'}
            type="button"
            onClick={() => onClick(x[labelKey] || '')}
            className="text-xs px-2 py-0.5 rounded-full border bg-white hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 dark:border-gray-700 flex items-center gap-1"
            title={showLabel ? x.label : ''}
          >
            <span className="font-mono">{x[labelKey] || '—'}</span>
            <span className="font-semibold text-red-600">{x.count}</span>
          </button>
        ))}
        {items.length === 0 && <span className="text-xs text-gray-400">Veri yok</span>}
      </div>
    </div>
  );
}
